import { useEffect, useRef } from "react";
import { Grid, Card, CardContent, Typography, Box } from "@material-ui/core";
import { Chart } from "chart.js";
import BigNumberCard from "components/_refactor/card/big-number";
import TableTitulosEmAtraso from "../components/table-titulos-em-atraso";
import MaskAndValid from "utils/maskAndValid";
import { Warning, Schedule, CheckCircle } from "@mui/icons-material";
import LoadingData from "components/Loading/LoadingData";

const parseCurrencyString = (value) => {
  if (!value) return 0;
  if (typeof value === "number") return value;

  const parsed = value.toString().replace(/\./g, "").replace(",", ".");
  return parseFloat(parsed) || 0;
};

export default function SituacaoFinanceira({ data, loading }) {
  const marcaChartRef = useRef(null);
  const marcaChartInstance = useRef(null);

  useEffect(() => {
    if (data?.ResumoMarca && data.ResumoMarca.length > 0) {
      requestAnimationFrame(() => {
        if (marcaChartRef.current) {
          renderMarcaChart();
        }
      });
    }

    return () => {
      if (marcaChartInstance.current) {
        marcaChartInstance.current.destroy();
        marcaChartInstance.current = null;
      }
    };
  }, [data]);

  const renderMarcaChart = () => {
    if (
      !data?.ResumoMarca ||
      data.ResumoMarca.length === 0 ||
      marcaChartRef.current === null
    )
      return;

    if (marcaChartInstance.current) {
      marcaChartInstance.current.destroy();
    }

    const ctx = marcaChartRef.current.getContext("2d");

    const labels = data.ResumoMarca.map((m) => m.Marca);
    const emAtrasoData = data.ResumoMarca.map((m) =>
      parseCurrencyString(m.ValorEmAtraso)
    );
    const aVencerData = data.ResumoMarca.map((m) =>
      parseCurrencyString(m.ValorAVencer)
    );

    const chartData = {
      labels: labels,
      datasets: [
        {
          label: "Em Atraso",
          data: emAtrasoData,
          backgroundColor: "#156082",
          borderColor: "#156082",
          borderWidth: 0,
        },
        {
          label: "A Vencer",
          data: aVencerData,
          backgroundColor: "#FF9800",
          borderColor: "#FF9800",
          borderWidth: 0,
        },
      ],
    };

    const options = {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: "bottom",
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              return `${context.dataset.label}: R$ ${MaskAndValid.maskPrice(
                context.raw
              )}`;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          beginAtZero: true,
          ticks: {
            callback: function (value) {
              return "R$ " + MaskAndValid.maskPrice(value);
            },
          },
          grid: {
            color: "#e5e5e5",
          },
        },
        y: {
          stacked: true,
          grid: {
            display: false,
          },
        },
      },
    };

    marcaChartInstance.current = new Chart(ctx, {
      type: "bar",
      data: chartData,
      options: options,
    });
  };

  if (loading) {
    return (
      <Grid container spacing={2} style={{ margin: "1rem 0 0" }}>
        <Grid item xs={12}>
          <LoadingData
            searching
            label="Buscando dados de situação financeira..."
          />
        </Grid>
      </Grid>
    );
  }

  const valorEmAtraso = parseCurrencyString(data?.ValorEmAtraso);
  const valorAVencer = parseCurrencyString(data?.ValorAVencer);
  const valorLiquidado = parseCurrencyString(data?.ValorLiquidado);
  const titulosEmAtraso = data?.TitulosEmAtraso || [];

  return (
    <Grid container spacing={2} style={{ margin: "1rem 0 0" }}>
      <Grid item xs={12} md={4}>
        <BigNumberCard
          title="Em Atraso"
          value={`R$ ${MaskAndValid.maskPrice(valorEmAtraso)}`}
          loading={loading}
          icon={<Warning fontSize="large" style={{ color: "#f44336" }} />}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <BigNumberCard
          title="A vencer"
          value={`R$ ${MaskAndValid.maskPrice(valorAVencer)}`}
          loading={loading}
          icon={<Schedule fontSize="large" style={{ color: "#156082" }} />}
          legend="Os títulos a vencer podem ser consultados na aba Títulos"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <BigNumberCard
          title="Liquidado"
          value={`R$ ${MaskAndValid.maskPrice(valorLiquidado)}`}
          loading={loading}
          icon={<CheckCircle fontSize="large" style={{ color: "#4CAF50" }} />}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h4">Marca</Typography>
            <Box position="relative" height="300px">
              <canvas ref={marcaChartRef}></canvas>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h4">Títulos em atraso</Typography>
            {titulosEmAtraso && titulosEmAtraso.length > 0 ? (
              <>
                <TableTitulosEmAtraso data={titulosEmAtraso} />
                <Typography variant="caption">
                  Para visualizar todos os título, acessar a opção de Títulos
                  Financeiros
                </Typography>
              </>
            ) : (
              <Typography variant="caption">
                Nenhum título em atraso encontrado
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
